import { io, Socket } from 'socket.io-client';

class WebSocketService {
  private socket: Socket | null = null;
  private readonly url: string;

  constructor() {
    // Use VITE_WS_URL if set, otherwise derive from current page origin
    // This ensures WebSocket works when accessing from LAN (e.g., iPhone)
    this.url = import.meta.env.VITE_WS_URL || window.location.origin;
  }

  connect() {
    // Reuse the existing singleton socket while it is connected or reconnecting.
    // If the socket object exists but is fully disconnected, explicitly restart it
    // so pages can recover after an outage without creating a duplicate socket.
    if (this.socket?.connected || this.socket?.active) {
      return;
    }

    if (this.socket) {
      this.socket.connect();
      return;
    }

    this.socket = io(this.url, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10
    });

    this.socket.on('connect', () => {
    });

    this.socket.on('disconnect', () => {
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
    });
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  on(event: string, callback: (data: any) => void) {
    if (!this.socket) {
      this.connect();
    }
    this.socket!.on(event, callback);
  }

  off(event: string, callback?: (data: any) => void) {
    if (!this.socket) return;
    if (callback) {
      this.socket.off(event, callback);
    } else {
      this.socket.off(event);
    }
  }

  emit(event: string, data: any) {
    if (!this.socket?.connected) {
      console.warn('WebSocket not connected');
      return;
    }
    this.socket.emit(event, data);
  }

  joinRoom(room: string) {
    this.emit('join-room', room);
  }

  leaveRoom(room: string) {
    this.emit('leave-room', room);
  }
}

// Export singleton instance
export const wsService = new WebSocketService();
